import { useEffect, useMemo, useRef, useState } from "react";
import { FORMULA_FUNCTIONS } from "../utils/formulaEngine";

// What the helper shows for each function (typing "cei" offers ceil( and ceiling( ; Enter / Tab inserts the name and the opening bracket).
const FUNCTION_HELP = {
  ceil: "ceil(x) — round up to a whole number",
  ceiling: "ceiling(x, step) — round up to a multiple of step (step optional, default 1)",
  floor: "floor(x, step) — round down to a multiple of step (step optional)",
  round: "round(x, digits) — round to digits places (optional, default 0)",
  abs: "abs(x) — the value without its sign",
  min: "min(a, b, …) — the smallest",
  max: "max(a, b, …) — the largest",
};
const FUNCTION_ENTRIES = Object.keys(FORMULA_FUNCTIONS).map(n => ({ name: n, insert: n + "(", unit: "fn", desc: FUNCTION_HELP[n] || n + "()", group: "function", groupTitle: "Function" }));
import { identifierAtCaret, suggestVariables, insertVariable, checkExpression, SAMPLE_ROOM_LABEL } from "../utils/formulaAssist";

// Excel-style expression editor for EstimateCostFormulas.Expression (Setup ▸
// Estimate Engine ▸ Formulas). As the author types an identifier a helper
// lists the matching variables (name · unit/value · meaning, grouped by
// where they bind) — ↑/↓ to move, Enter/Tab to insert, Esc to dismiss — and
// beneath the input the whole expression is checked live against a sample
// room: the number it would produce, or exactly which variable is unknown /
// where the syntax breaks. Pure logic lives in src/utils/formulaAssist.js.
//
//   variables — flat list from flattenVariableGroups(buildFormulaVariableGroups(rows))
//   bindings  — live Engine Variable values ({ KEY: number }) for the preview
//   bound / sampleLabel — (optional) for a formula that is NOT room-bound (an assembly component formula): the exact values it
//   will see, and the wording of the live result ("for qty = 25 LF" instead of the sample room)
//   hideStatus — (optional) the caller shows the live result itself, using formulaStatus()
//   evaluateAs — (optional) maps the typed text to what is actually evaluated (an assembly component's plain number 4 means qty * 4)
export default function FormulaExpressionEditor({ value, onChange, variables, bindings = {}, placeholder, style, disabled, bound = null, sampleLabel = null, evaluateAs = null, inputId = undefined, hideStatus = false }) {
  const inputRef = useRef(null);
  const [caret, setCaret] = useState((value || "").length);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const pendingCaret = useRef(null);

  const text = value || "";
  const ident = open ? identifierAtCaret(text, caret) : null;
  const suggestables = useMemo(() => [...FUNCTION_ENTRIES, ...variables], [variables]);
  const suggestions = useMemo(() => (ident ? suggestVariables(ident.word, 8, suggestables) : []), [ident?.word, suggestables]);
  const check = useMemo(() => checkExpression(evaluateAs ? evaluateAs(text) : text, { vars: variables, bindings, bound }), [text, variables, bindings, bound, evaluateAs]);

  useEffect(() => { if (active >= suggestions.length) setActive(0); }, [suggestions.length, active]);
  // Restore the caret after an insert re-renders the controlled input.
  useEffect(() => {
    if (pendingCaret.current == null || !inputRef.current) return;
    inputRef.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  }, [text]);

  const syncCaret = (el) => setCaret(el.selectionStart ?? el.value.length);
  const pick = (entry) => {
    const next = insertVariable(text, caret, entry.insert ?? entry.name);
    pendingCaret.current = next.caret;
    setCaret(next.caret);
    onChange(next.text);
    setOpen(false);
    inputRef.current?.focus();
  };
  const onKeyDown = (e) => {
    if (!suggestions.length) { if (e.key === "Escape") setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => (a + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => (a - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(suggestions[active] || suggestions[0]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const status = formulaStatus(check, { bound, sampleLabel });
  const tone = { ok: "#166534", warn: "#b45309", bad: "#b91c1c" };

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        id={inputId}
        value={text}
        disabled={disabled}
        placeholder={placeholder || "e.g. floorArea * 1.05   ·   perimeter - doorCount * 3   ·   supplyCfm / HEPA_DEFAULT_DESIGN_CFM"}
        onChange={e => { onChange(e.target.value); syncCaret(e.target); setOpen(true); setActive(0); }}
        onKeyDown={onKeyDown}
        onKeyUp={e => { if (!["ArrowUp", "ArrowDown", "Enter", "Tab", "Escape"].includes(e.key)) syncCaret(e.target); }}
        onClick={e => { syncCaret(e.target); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        spellCheck={false} autoComplete="off"
        style={{ ...style, fontFamily: "monospace", width: "100%", boxSizing: "border-box" }}
      />
      {open && suggestions.length > 0 && (
        <div role="listbox" style={{ position: "absolute", zIndex: 30, left: 0, right: 0, top: "100%", marginTop: 2, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 6, boxShadow: "0 6px 20px rgba(0,0,0,0.12)", maxHeight: 260, overflowY: "auto" }}>
          {suggestions.map((v, i) => (
            <div key={`${v.group}:${v.name}`} role="option" aria-selected={i === active}
              onMouseDown={e => { e.preventDefault(); pick(v); }}
              onMouseEnter={() => setActive(i)}
              style={{ display: "grid", gridTemplateColumns: "minmax(140px, auto) 60px 1fr", gap: 10, alignItems: "baseline", padding: "5px 10px", cursor: "pointer", background: i === active ? "var(--tint-blue)" : "transparent", borderTop: i ? "1px solid var(--cs-border)" : "none" }}>
              <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12 }}>{v.name}</span>
              <span style={{ fontSize: 11, color: "var(--text3)" }}>{v.unit || "—"}</span>
              <span style={{ fontSize: 11, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v.desc}>
                {v.desc}<span style={{ color: "var(--text3)" }}> · {v.groupTitle}</span>
              </span>
            </div>
          ))}
          <div style={{ padding: "3px 10px", fontSize: 10, color: "var(--text3)", borderTop: "1px solid var(--cs-border)" }}>↑ ↓ to move · Enter or Tab to insert · Esc to close</div>
        </div>
      )}
      {status && !hideStatus && <div style={{ marginTop: 4, fontSize: 11, color: tone[status.tone], fontFamily: status.tone === "ok" ? "inherit" : "inherit" }}>{status.tone === "ok" ? "✓ " : status.tone === "warn" ? "⚠ " : "✗ "}{status.text}</div>}
    </div>
  );
}

// The live result of a checkExpression() as { tone: 'ok' | 'warn' | 'bad', text } (null for an empty box). Exported so a caller can place
// it somewhere other than under the input (the assembly editor puts it beside the formula).
export function formulaStatus(check, { bound = null, sampleLabel = null } = {}) {
  if (check.empty) return null;
  if (check.unknownFunctions?.length) return { tone: "bad", text: `Unknown function: ${check.unknownFunctions.map(n => `${n}()`).join(", ")} — available: ${Object.keys(FORMULA_FUNCTIONS).map(n => `${n}()`).join(" ")}.` };
  if (check.unknown.length) return { tone: "bad", text: `Unknown variable${check.unknown.length > 1 ? "s" : ""}: ${check.unknown.join(", ")} — pick from the helper or the Formula Variables list below.` };
  if (check.error) return { tone: "bad", text: check.error };
  if (check.mixed) return { tone: "warn", text: `Evaluates to ${fmt(check.value)}, but mixes room and circuit/assembly variables — those are never bound together, so it will not resolve in use.` };
  return { tone: "ok", text: `= ${fmt(check.value)} for ${sampleLabel || SAMPLE_ROOM_LABEL}${!bound && (check.groups.includes("circuit") || check.groups.includes("assembly")) ? " (circuit/assembly values are sample figures)" : ""}` };
}

function fmt(v) {
  if (v == null || !Number.isFinite(v)) return "—";
  return Math.abs(v) >= 100 ? Math.round(v).toLocaleString() : (Math.round(v * 100) / 100).toLocaleString();
}
